/**
 * Retry Interceptor for Failed Requests
 * 
 * Automatically retries failed requests with exponential backoff
 * 
 * Rules:
 * - Only retries network errors and 5xx server errors
 * - NEVER retries CORS errors (prevents infinite loops)
 * - NEVER retries 4xx client errors (except 429)
 * - Uses exponential backoff: 1s, 2s, 4s
 * - Maximum 3 retries
 * 
 * @module retry-interceptor
 */

import { AxiosError } from 'axios';
import { axiosInstance, DrupalAxiosRequestConfig } from '@core/config/axios.config';

/**
 * Retry configuration interface
 */
interface RetryConfig extends DrupalAxiosRequestConfig {
    retries?: number;
    retryDelay?: number;
    retryCount?: number;
    _retry?: boolean; // Prevent infinite loops
}

/**
 * Default retry configuration
 */
const DEFAULT_RETRY_CONFIG = {
    retries: 3,
    retryDelay: 1000, // 1 second base delay
    retryCount: 0,
} as const;

/**
 * Check if error is a CORS error (should NEVER be retried)
 * Note: ECONNABORTED (timeout) is NOT a CORS error and should be retried
 */
const isCorsError = (error: AxiosError): boolean => {
    if (!error.response) {
        const message = error.message?.toLowerCase() || '';
        return (
            message.includes('cors') ||
            (message.includes('network error') && !error.code) ||
            message.includes('failed to fetch') ||
            error.code === 'ERR_NETWORK'
        );
    }
    return false;
};

/**
 * Check if error is a timeout error (should be retried)
 */
const isTimeoutError = (error: AxiosError): boolean => {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return true;
    }
    // Check if response status is 504 (Gateway Timeout)
    if (error.response?.status === 504) {
        return true;
    }
    return false;
};

/**
 * Determine if a request should be retried based on error
 * 
 * Retry conditions:
 * - Timeout errors (ECONNABORTED, 504 Gateway Timeout)
 * - Network errors (no response, but not CORS)
 * - 5xx server errors (500, 502, 503, 504)
 * - 429 Too Many Requests (rate limiting)
 * 
 * Never retry:
 * - CORS errors
 * - 4xx client errors (except 429)
 * - Requests with skipRetry flag
 */
const shouldRetry = (error: AxiosError, config: RetryConfig): boolean => {
    // Don't retry if skipRetry flag is set
    if (config.skipRetry) {
        if (import.meta.env.DEV) {
            console.log('⏭️ Skipping retry (skipRetry flag set)');
        }
        return false;
    }

    // NEVER retry CORS errors (prevents infinite loops)
    if (isCorsError(error)) {
        if (import.meta.env.DEV) {
            console.log('🚫 CORS error - not retryable');
        }
        return false;
    }

    // Retry timeout errors (these are retryable)
    if (isTimeoutError(error)) {
        if (import.meta.env.DEV) {
            console.log('⏱️ Timeout error - will retry');
        }
        return true;
    }

    // Network error (no response) - retry
    if (!error.response) {
        return true;
    }

    const status = error.response.status;

    // Retry 5xx server errors
    if (status >= 500 && status <= 599) {
        return true;
    }

    // Retry 429 Too Many Requests (rate limiting)
    if (status === 429) {
        return true;
    }

    // Don't retry 4xx client errors (except 429)
    return false;
};

/**
 * Calculate exponential backoff delay
 * 
 * Formula: baseDelay * 2^(retryCount - 1)
 * Examples:
 * - Retry 1: 1s * 2^0 = 1s
 * - Retry 2: 1s * 2^1 = 2s
 * - Retry 3: 1s * 2^2 = 4s
 */
const calculateDelay = (retryCount: number, baseDelay: number): number => {
    return baseDelay * Math.pow(2, retryCount - 1);
};

/**
 * Retry Interceptor
 * 
 * Automatically retries failed requests with exponential backoff
 * 
 * IMPORTANT: This runs BEFORE the error interceptor
 * (interceptors execute in reverse registration order)
 */
export const retryInterceptor = async (error: AxiosError): Promise<any> => {
    const config = error.config as RetryConfig;

    // If no config, can't retry
    if (!config) {
        return Promise.reject(error);
    }

    // Initialize retry config if not present
    if (config.retryCount === undefined) {
        config.retries = DEFAULT_RETRY_CONFIG.retries;
        config.retryDelay = DEFAULT_RETRY_CONFIG.retryDelay;
        config.retryCount = 0;
    }

    // Check if should retry
    if (!shouldRetry(error, config)) {
        return Promise.reject(error);
    }

    // Check if max retries reached
    if (config.retryCount >= (config.retries || DEFAULT_RETRY_CONFIG.retries)) {
        if (import.meta.env.DEV) {
            console.log(
                `❌ Max retries (${config.retries}) reached for ${config.url}`
            );
        }
        return Promise.reject(error);
    }

    // Increment retry count
    config.retryCount += 1;

    // Calculate delay with exponential backoff
    const delay = calculateDelay(
        config.retryCount,
        config.retryDelay || DEFAULT_RETRY_CONFIG.retryDelay
    );

    // Log retry attempt in development
    if (import.meta.env.DEV) {
        const status = error.response?.status || 'Network Error';
        console.log(
            `🔄 Retrying request (${config.retryCount}/${config.retries}) after ${delay}ms - ${config.method?.toUpperCase()} ${config.url} (${status})`
        );
    }

    // Wait before retrying
    await new Promise((resolve) => setTimeout(resolve, delay));

    // Retry request with same config
    return axiosInstance(config);
};

/**
 * Initialize retry interceptor
 * 
 * IMPORTANT: This must be called AFTER initializeInterceptors()
 * in App.tsx to ensure proper execution order:
 * 
 * 1. Retry interceptor (registered last, runs first)
 * 2. Error interceptor (registered first, runs second)
 */
let retryInterceptorInitialized = false;

export const initializeRetryInterceptor = (): void => {
    if (retryInterceptorInitialized) {
        return;
    }
    retryInterceptorInitialized = true;

    axiosInstance.interceptors.response.use(
        (response) => response,
        retryInterceptor
    );

    if (import.meta.env.DEV) {
        console.log('✅ Retry interceptor initialized');
    }
};
